import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { freeToolsAPI, jobTrackerAPI, authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CreditCounter, PaymentModal } from './CreditComponents';
import logo from '../assets/logo.png';

// Embedded MatchLite Component for Hero Section
const EmbeddedMatchLite = () => {
  const { isAuthenticated, initiateGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    jobDescription: '',
    resumeText: '',
    inputMethod: 'text',
    consentGiven: false
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingToTracker, setSavingToTracker] = useState(false);
  const [savedToTracker, setSavedToTracker] = useState(false);

  // Handle Google Login for non-authenticated users
  const handleLoginToScan = () => {
    // Store intent to return to home page after login
    localStorage.setItem('login_redirect', 'match_score');
    // Trigger Google OAuth
    authAPI.initiateGoogleLogin('/auth/callback');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.docx')) {
        setError('Please upload a PDF or DOCX file.');
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        e.target.value = '';
        return;
      }
      setResumeFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consentGiven) {
      setError('You must agree to the Privacy Policy to proceed.');
      return;
    }

    if (formData.jobDescription.length < 200) {
      setError('Job description must be at least 200 characters for accurate matching.');
      return;
    }

    if (formData.inputMethod === 'file') {
      if (!resumeFile) {
        setError('Please upload a resume file.');
        return;
      }
    } else {
      if (formData.resumeText.length < 50) {
        setError('Resume text must be at least 50 characters.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setRateLimited(false);
    setResults(null);

    try {
      const response = await freeToolsAPI.matchLiteV2(
        formData.jobDescription,
        formData.inputMethod === 'text' ? formData.resumeText : null,
        formData.inputMethod === 'file' ? resumeFile : null
      );
      setResults(response);
      
      // Track analytics
      freeToolsAPI.trackAnalyticsEvent('match-lite_completed', {
        score: response.match_score,
        source: 'homepage_hero'
      }).catch(() => {});

      // Trigger credit update
      window.dispatchEvent(new Event('credit_update'));
      
    } catch (err) {
      if (err.response?.status === 429) {
        setRateLimited(true);
        setError('Daily limit reached. Try again tomorrow or upgrade for unlimited access.');
      } else if (err.response?.status === 402) {
        setShowPaymentModal(true);
        setError(null);
      } else if (err.response?.status === 413) {
        setError('File too large. Please upload a file smaller than 5MB.');
      } else {
        const detail = err.response?.data?.detail;
        const errorMessage = typeof detail === 'object' ? JSON.stringify(detail) : (detail || 'Analysis failed. Please try again.');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setResults(null);
    setFormData({
      jobDescription: '',
      resumeText: '',
      inputMethod: 'text',
      consentGiven: false
    });
    setResumeFile(null);
    setError(null);
    setSavedToTracker(false);
  };

  // Save job to tracker
  const handleSaveToTracker = async () => {
    if (!isAuthenticated) {
      // Redirect to login first
      navigate('/tracker');
      return;
    }

    setSavingToTracker(true);
    try {
      console.log('[SaveToTracker] Starting save...');
      console.log('[SaveToTracker] Results:', results);
      
      // Extract company and role from job description (simple heuristic)
      const jobText = formData.jobDescription;
      let company = 'Company';
      let role = 'Role';
      
      // Try to extract role from common patterns
      const rolePatterns = [
        /(?:position|role|title|hiring|looking for)[:\s]+([^\n.]+)/i,
        /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+(?:Engineer|Developer|Manager|Designer|Analyst|Specialist|Lead|Director|Associate))?)/m
      ];
      for (const pattern of rolePatterns) {
        const match = jobText.match(pattern);
        if (match) {
          role = match[1].trim().slice(0, 100);
          break;
        }
      }

      // Try to extract company name
      const companyPatterns = [
        /(?:at|@|company)[:\s]+([^\n.]+)/i,
        /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+(?:Inc|LLC|Corp|Ltd|Company))?)\s+is\s+(?:hiring|looking|seeking)/i
      ];
      for (const pattern of companyPatterns) {
        const match = jobText.match(pattern);
        if (match) {
          company = match[1].trim().slice(0, 100);
          break;
        }
      }

      // Handle both old and new API response formats
      const missingSkills = results.key_missing_skills || results.missing_skills || [];
      const summary = results.summary_verdict || results.summary || '';
      
      const applicationData = {
        company: company,
        role: role,
        job_description: formData.jobDescription.slice(0, 5000),
        status: 'Draft',
        fit_score: results.match_score,
        notes: `Match Score: ${results.match_score}%\n\nMissing Skills: ${missingSkills.join(', ') || 'None'}\n\nSummary: ${summary}`
      };
      
      console.log('[SaveToTracker] Creating application:', applicationData);
      
      // Create job application
      const response = await jobTrackerAPI.createApplication(applicationData);
      console.log('[SaveToTracker] Success:', response);

      setSavedToTracker(true);
      
      // Redirect to tracker after a brief delay
      setTimeout(() => {
        navigate('/tracker');
      }, 1000);
      
    } catch (err) {
      console.error('[SaveToTracker] Error:', err);
      console.error('[SaveToTracker] Response:', err.response?.data);
      
      if (err.response?.status === 401) {
        setError('Please sign in to save to tracker.');
      } else {
        setError(err.response?.data?.detail || 'Failed to save to tracker. Please try again.');
      }
    } finally {
      setSavingToTracker(false);
    }
  };

  // Results View
  if (results) {
    const scoreColor = results.match_score >= 70 ? 'text-green-600' : results.match_score >= 50 ? 'text-amber-600' : 'text-red-600';
    const scoreBg = results.match_score >= 70 ? 'bg-green-50 border-green-200' : results.match_score >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
    
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        {/* Score Display */}
        <div className={`${scoreBg} rounded-xl p-6 mb-6 border text-center`}>
          <p className="text-sm text-gray-600 mb-2">Your Match Score</p>
          <p className={`text-6xl font-bold ${scoreColor}`}>{results.match_score}%</p>
          <p className="text-sm text-gray-500 mt-2">
            {results.match_score >= 70 ? 'Strong match!' : results.match_score >= 50 ? 'Good potential with improvements' : 'Needs optimization'}
          </p>
        </div>

        {/* Summary */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
          <p className="text-gray-600 text-sm">{results.summary_verdict}</p>
        </div>

        {/* Missing Skills */}
        {results.key_missing_skills && results.key_missing_skills.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Missing Keywords</h4>
            <div className="flex flex-wrap gap-2">
              {results.key_missing_skills.map((skill, index) => (
                <span key={index} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm border border-red-200">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Upsell */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-100">
          <p className="text-sm text-blue-800">{results.upsell_hook}</p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Save to Tracker Button */}
          <button
            onClick={handleSaveToTracker}
            disabled={savingToTracker || savedToTracker}
            className={`w-full py-3 px-4 font-medium rounded-lg transition-colors flex items-center justify-center ${
              savedToTracker
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {savingToTracker ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : savedToTracker ? (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Saved! Redirecting to Dashboard...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                {isAuthenticated ? 'Save this Job to Tracker' : 'Sign In to Save to Tracker'}
              </>
            )}
          </button>

          <button
            onClick={resetForm}
            className="w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            Analyze Another Resume
          </button>
        </div>
      </div>
    );
  }

  // Form View
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
      {/* Rate Limited Banner */}
      {rateLimited && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">⚠️ Daily limit reached. Upgrade for unlimited access.</p>
        </div>
      )}

      {/* Error Display */}
      {error && !rateLimited && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        {/* Payment Modal */}
        <PaymentModal 
          isOpen={showPaymentModal} 
          onClose={() => setShowPaymentModal(false)} 
          creditsRemaining={0}
        />

        {/* Job Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Job Description
          </label>
          <textarea
            name="jobDescription"
            value={formData.jobDescription}
            onChange={handleInputChange}
            placeholder="Paste the full job description for best results (minimum 200 characters)"
            rows={4}
            autoComplete="off"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">{formData.jobDescription.length} / 200 min</p>
        </div>

        {/* Resume Input Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Your Resume
          </label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, inputMethod: 'text' }))}
              className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                formData.inputMethod === 'text'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              Paste Text
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, inputMethod: 'file' }))}
              className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                formData.inputMethod === 'file'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              Upload File
            </button>
          </div>

          {formData.inputMethod === 'text' ? (
            <textarea
              name="resumeText"
              value={formData.resumeText}
              onChange={handleInputChange}
              placeholder="Paste your resume content here..."
              rows={4}
              autoComplete="off"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            />
          ) : (
            <label htmlFor="resume-file-hero" className="block border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                className="hidden"
                id="resume-file-hero"
              />
              {resumeFile ? (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-700">{resumeFile.name}</span>
                </div>
              ) : (
                <>
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-500">Click to upload PDF or DOCX (max 5MB)</p>
                </>
              )}
            </label>
          )}
        </div>

        {/* Consent */}
        <div className="flex items-start">
          <input
            type="checkbox"
            name="consentGiven"
            checked={formData.consentGiven}
            onChange={handleInputChange}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="ml-2 text-xs text-gray-500">
            I agree to the{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
            . My data is processed securely and never stored.
          </label>
        </div>

        {/* Submit Button */}
        {isAuthenticated ? (
          <button
            type="submit"
            disabled={loading || rateLimited}
            className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyzing...
              </>
            ) : (
              'Check My Match Score'
            )}
          </button>
        ) : (
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleLoginToScan}
              className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google to Scan
            </button>
            <p className="text-center text-xs text-gray-400">
              Login with Google to unlock your 5 free lifetime credits
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

// Main Home Component
const Home = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();

  // No longer auto-redirect to tracker - show ToolHub instead for logged-in users
  // This gives users flexibility to access all tools, not just the tracker

  const handleTrackerClick = () => {
    navigate('/tracker');
  };

  // Sign In button - directly triggers Google login
  const handleSignIn = () => {
    authAPI.initiateGoogleLogin('/auth/callback');
  };

  // Show loading while checking auth (prevents flash of home page)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If authenticated, show ToolHub instead of landing page
  if (isAuthenticated) {
    const ToolHub = require('./ToolHub').default;
    return <ToolHub />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation - Landing Page */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[60px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/tools/future-proof" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:flex items-center gap-1">
                Future-Proof
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] font-bold rounded-full">NEW</span>
              </Link>
              <button
                onClick={handleTrackerClick}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:block"
              >
                Job Tracker
              </button>
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              <Link
                to="/tracker"
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Background Image */}
      <section className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?auto=format&fit=crop&w=2000&q=80')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-slate-900/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Copy */}
            <div className="lg:pr-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
                Find out if your resume{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  aligns
                </span>{' '}
                with the job you want.
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-gray-300 leading-relaxed">
                Don&apos;t guess.{' '}
                <button 
                  onClick={() => {
                    localStorage.setItem('login_redirect', 'match_score');
                    authAPI.initiateGoogleLogin('/auth/callback');
                  }}
                  className="font-semibold text-white underline decoration-2 underline-offset-4 hover:text-blue-300 transition-colors cursor-pointer"
                >
                  Sign in with Google
                </button>
                {' '}to get 5 free credits—enough to run your first Future-Proof Audit on us—<span className="font-medium text-white">instantly.</span>
              </p>
              
              {/* Trust Badge - VIBRANT */}
              <div className="mt-8 inline-flex items-center px-5 py-3 bg-green-600 text-white font-bold rounded-full shadow-lg border-2 border-green-400">
                <span className="text-xl mr-2">✅</span>
                <span className="text-base">
                  Zero-Retention Guarantee
                </span>
                <span className="text-sm text-green-100 ml-2 hidden sm:inline">
                  — We process in RAM and delete instantly. No database.
                </span>
              </div>

              {/* Social Proof - Updated for dark background */}
              <div className="mt-8 flex items-center space-x-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">10K+</p>
                  <p className="text-xs text-gray-400">Resumes Analyzed</p>
                </div>
                <div className="h-8 w-px bg-gray-600"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">Free</p>
                  <p className="text-xs text-gray-400">No Credit Card</p>
                </div>
                <div className="h-8 w-px bg-gray-600"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">30s</p>
                  <p className="text-xs text-gray-400">Instant Results</p>
                </div>
              </div>
            </div>

            {/* Right: MatchLite Tool */}
            <div className="lg:pl-4">
              <EmbeddedMatchLite />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - "The Black Hole" */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Stop Applying into the Void
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Understand why your applications aren&apos;t getting responses.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1: ATS Wall */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">🤖</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The ATS Wall</h3>
              <p className="text-gray-600">
                Companies use Applicant Tracking Systems to filter candidates. If you miss key keywords, you get auto-rejected.
              </p>
            </div>

            {/* Card 2: 60% Rule */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">📉</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The 60% Rule</h3>
              <p className="text-gray-600">
                Most recruiters only look at candidates who match at least 60% of the job description skills. Do you?
              </p>
            </div>

            {/* Card 3: Ghosting */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">👻</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The Ghosting</h3>
              <p className="text-gray-600">
                Applying blindly leads to months of silence. Fix your resume before you apply to get more interviews.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Tools to Get You Hired
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to optimize your job search—starting for free.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Feature 1: Match Lite */}
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">FREE</span>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Instant Gap Analysis</h3>
              <p className="text-gray-600 mb-4">
                Paste a job description and your resume. We&apos;ll show you exactly which hard skills you&apos;re missing so you can fix them fast.
              </p>
              <a href="#hero" className="text-blue-600 font-medium text-sm hover:text-blue-700 inline-flex items-center">
                Try it above
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </a>
            </div>

            {/* Feature 2: Job Tracker */}
            <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">FREE</span>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Organize Your Search</h3>
              <p className="text-gray-600 mb-4">
                Stop using messy spreadsheets. Track up to 20 active applications in a private dashboard connected to your Google Drive.
              </p>
              <button
                onClick={handleTrackerClick}
                className="text-purple-600 font-medium text-sm hover:text-purple-700 inline-flex items-center"
              >
                Launch Tracker
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>

            {/* Feature 3: Privacy First */}
            <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Data, Your Control</h3>
              <p className="text-gray-600 mb-4">
                Unlike other free tools, we don&apos;t harvest your resume. Our &quot;Ephemeral Processing&quot; means your data disappears the moment you close the tab.
              </p>
              <Link to="/privacy" className="text-green-600 font-medium text-sm hover:text-green-700 inline-flex items-center">
                Learn more
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Three simple steps to optimize your resume.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload & Scan</h3>
              <p className="text-gray-600">
                Drag your resume into our secure, memory-only scanner. We never store your files.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Your Score</h3>
              <p className="text-gray-600">
                See your 0-100% match rating instantly, plus the exact keywords you&apos;re missing.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Optimize & Apply</h3>
              <p className="text-gray-600">
                Add the missing keywords and send your application with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Job Seekers Are Saying
            </h2>
            <p className="text-lg text-gray-600">
              Real feedback from people navigating their job search
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Finally, a platform that brings back sanity to job searching. No more chaos—just clarity."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                  M
                </div>
                <div>
                  <p className="font-medium text-gray-900">Michelle T.</p>
                  <p className="text-sm text-gray-500">Marketing Professional</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Makes it so much easier to navigate the challenges of looking for a job. I actually feel organized for the first time."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-semibold">
                  J
                </div>
                <div>
                  <p className="font-medium text-gray-900">James K.</p>
                  <p className="text-sm text-gray-500">Software Engineer</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "I was applying everywhere with no system. This tracker changed how I approach my search—intentional, not desperate."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold">
                  S
                </div>
                <div>
                  <p className="font-medium text-gray-900">Sarah L.</p>
                  <p className="text-sm text-gray-500">Career Changer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to land your next role?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the job seekers who stopped guessing and started aligning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleTrackerClick}
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
            >
              Launch Job Tracker
            </button>
            <Link
              to="/tools/cover-letter"
              className="px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-400 transition-colors border border-blue-400"
            >
              Try Cover Letter Starter
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
